import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import "../styles/Checkout.css";
// import SubscriberProfile from "../admin/pages/SubscriberProfile";

type PaymentMethod =
  | "cash_on_delivery"
  | "credit_debit_card"
  | "online_banking"
  | "google_pay";

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const { showModal } = useMessageModal();

  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [userId, setUserId] = useState();

  const [deliveryAddress, setDeliveryAddress] = useState(
    "4738, Jalan PJU 10/1, Damansara Damai, 47830 Petaling Jaya, Selangor, Malaysia",
  );
  const [isEditingDelivery, setIsEditingDelivery] = useState(false);
  const [messageToSeller, setMessageToSeller] = useState("");
  const [shippingOption, setShippingOption] = useState<"standard" | "express">(
    "standard",
  );
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>("online_banking");
  const [usePromotion, setUsePromotion] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const subscriberInfo = JSON.parse(
      localStorage.getItem("subscriber_user") || "",
    );
    setBuyerName(subscriberInfo.name);
    setBuyerEmail(subscriberInfo.email);
    setBuyerPhone(subscriberInfo.phoneNo);
    setDeliveryAddress(subscriberInfo.address);
    setUserId(subscriberInfo.id);
  }, []);

  const merchandiseSubtotal = useMemo(() => {
    return cartItems.reduce(
      (sum, item) => sum + Number(item.active_price || 0) * item.quantity,
      0,
    );
  }, [cartItems]);

  const shippingFee = useMemo(() => {
    if (!cartItems.length) return 0;
    return shippingOption === "express" ? 12.0 : 6.0;
  }, [cartItems.length, shippingOption]);

  const discountAmount = useMemo(() => {
    if (!usePromotion) return 0;
    return 5.0;
  }, [usePromotion]);

  const totalAmount = merchandiseSubtotal + shippingFee - discountAmount;

  const formatCurrency = (value: number) =>
    `RM${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const getProductImage = (item: any) => {
    if (item.image_path) {
      return `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/products/${
        item.id
      }/${item.image_path}`;
    }

    if (item.image) return item.image;

    return "/placeholder-product.png";
  };

  const redirectToSecurePay = (
    actionUrl: string,
    params: Record<string, string>,
  ) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = actionUrl;

    Object.entries(params).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = String(value ?? "");
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const handleCheckout = async () => {
    try {
      if (!buyerName || !buyerEmail || !buyerPhone) {
        // alert("Please complete buyer information.");
        showModal({
          type: "warning",
          title: "Warning",
          message: "Please complete buyer information.",
          confirmText: "OK",
        });
        return;
      }

      if (!deliveryAddress.trim()) {
        // alert("Please enter delivery address.");
        showModal({
          type: "warning",
          title: "Warning",
          message: "Please enter delivery address.",
          confirmText: "OK",
        });
        return;
      }

      if (!cartItems.length) {
        // alert("Your cart is empty.");
        showModal({
          type: "warning",
          title: "Warning",
          message: "Your cart is empty.",
          confirmText: "OK",
        });
        return;
      }

      setLoading(true);

      // Cash on Delivery flow (frontend only for now)
      if (paymentMethod === "cash_on_delivery") {
        const codResponse = await fetch(
          `${import.meta.env.VITE_LYRAZONE_BASE_URL}/payments/cod/create`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              buyerName,
              buyerEmail,
              buyerPhone,
              deliveryAddress,
              messageToSeller,
              shippingOption,
              paymentMethod: "Cash on Delivery",
              cartItems,
              amount: totalAmount,
              userId: userId,
            }),
          },
        );

        const codData = await codResponse.json();

        if (!codResponse.ok || !codData.success) {
          // alert(codData.message || "Unable to create Cash on Delivery order.");
          showModal({
            type: "error",
            title: "Error",
            message:
              codData.message || "Unable to create Cash on Delivery order.",
            confirmText: "OK",
          });
          return;
        }

        navigate(
          `/payment-status?orderId=${encodeURIComponent(
            codData.data.orderId,
          )}&status=pending&paymentMethod=${encodeURIComponent(
            "Cash on Delivery",
          )}&amount=${encodeURIComponent(codData.data.amount)}&message=${encodeURIComponent(
            "Order placed successfully with Cash on Delivery.",
          )}`,
        );

        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_LYRAZONE_BASE_URL}/payments/securepay/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            buyerName,
            buyerEmail,
            buyerPhone,
            deliveryAddress,
            messageToSeller,
            shippingOption,
            // paymentMethod:
            //   paymentMethod === "credit_debit_card"
            //     ? "Credit / Debit Card"
            //     : paymentMethod === "online_banking"
            //       ? "Online Banking"
            //       : paymentMethod === "google_pay"
            //         ? "Google Pay"
            //         : "Cash on Delivery",
            // cartItems,
            paymentMethod:
              paymentMethod === "online_banking"
                ? "Online Banking"
                : "Cash on Delivery",
            cartItems,
            amount: totalAmount,
            userId: userId,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        // alert(data.message || "Unable to initiate payment.");
        showModal({
          type: "error",
          title: "error",
          message: data.message || "Unable to initiate payment.",
          confirmText: "OK",
        });
        return;
      }

      if (data?.formAction && data?.securepayPayload) {
        redirectToSecurePay(data.formAction, data.securepayPayload);
      } else {
        // alert("Invalid payment response received.");
        showModal({
          type: "error",
          title: "Failed",
          message: "Invalid payment response received.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Checkout error:", error);
      // alert("Checkout failed.");
      showModal({
        type: "error",
        title: "failed",
        message: "Checkout failed.",
        confirmText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-topbar">
          <div className="checkout-topbar-left">
            <h2>Checkout</h2>
            <span>{cartItems.length} item(s)</span>
          </div>

          <button
            type="button"
            className="checkout-back-btn"
            onClick={() => navigate("/shopping-cart")}
          >
            Back to Cart
          </button>
        </div>

        <div className="checkout-card checkout-address-card">
          <div className="checkout-card-title-row">
            <h3>📍 Delivery Address</h3>
            <span
              className="checkout-change-text"
              onClick={() => setIsEditingDelivery((prev) => !prev)}
              style={{ cursor: "pointer" }}
            >
              {isEditingDelivery ? "Save" : "Change"}
            </span>
          </div>

          <div className="checkout-address-grid">
            <div className="checkout-buyer-inline">
              <input
                type="text"
                placeholder="Buyer Name"
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
                className="checkout-input"
                disabled
              />
              <input
                type="email"
                placeholder="Buyer Email"
                value={buyerEmail}
                onChange={(e) => setBuyerEmail(e.target.value)}
                className="checkout-input"
                disabled
              />
              <input
                type="text"
                placeholder="Buyer Phone"
                value={buyerPhone}
                onChange={(e) => setBuyerPhone(e.target.value)}
                className="checkout-input"
                disabled={!isEditingDelivery}
              />
            </div>

            <textarea
              placeholder="Delivery Address"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              className="checkout-textarea"
              rows={3}
              disabled={!isEditingDelivery}
            />
          </div>
        </div>

        <div className="checkout-card">
          <div className="checkout-card-title-row checkout-products-head">
            <h3>Products Ordered</h3>
            <div className="checkout-products-columns">
              <span>Unit Price</span>
              <span>Amount</span>
              <span>Item Subtotal</span>
            </div>
          </div>

          {cartItems.length === 0 ? (
            <div className="checkout-empty">Your cart is empty.</div>
          ) : (
            <div className="checkout-products-list">
              {cartItems.map((item, index) => {
                const unitPrice = Number(item.active_price || 0);
                const itemSubtotal = unitPrice * item.quantity;

                return (
                  <div className="checkout-product-row" key={item.id || index}>
                    <div className="checkout-product-main">
                      <div className="checkout-product-image-wrap">
                        <img
                          src={getProductImage(item)}
                          alt={item.productName || "Product"}
                          className="checkout-product-image"
                        />
                      </div>

                      <div className="checkout-product-info">
                        <h4>{item.productName || "Product Name"}</h4>
                        {item.barCode && (
                          <p className="checkout-product-meta">
                            Barcode: {item.barCode}
                          </p>
                        )}
                        <p className="checkout-product-meta">
                          Variation: {item.variant || "Default"}
                        </p>
                      </div>
                    </div>

                    <div className="checkout-product-price">
                      {formatCurrency(unitPrice)}
                    </div>
                    <div className="checkout-product-qty">{item.quantity}</div>
                    <div className="checkout-product-subtotal">
                      {formatCurrency(itemSubtotal)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="checkout-promo-row">
            <label className="checkout-checkbox-row">
              <input
                type="checkbox"
                checked={usePromotion}
                onChange={(e) => setUsePromotion(e.target.checked)}
              />
              <span>Use Promotion</span>
            </label>
          </div>

          <div className="checkout-lower-grid">
            <div className="checkout-message-box">
              <label>Message for Seller</label>
              <input
                type="text"
                placeholder="Place a note here..."
                value={messageToSeller}
                onChange={(e) => setMessageToSeller(e.target.value)}
                className="checkout-input"
              />
            </div>

            <div className="checkout-shipping-box">
              <div className="checkout-shipping-header">
                <span>Shipping Option</span>
                <span className="checkout-change-text">Change</span>
              </div>

              <div className="checkout-shipping-options">
                <label
                  className={`checkout-shipping-option ${
                    shippingOption === "standard" ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="shippingOption"
                    value="standard"
                    checked={shippingOption === "standard"}
                    onChange={() => setShippingOption("standard")}
                  />
                  <div>
                    <strong>Standard Delivery</strong>
                    <p>Guaranteed in 5 - 7 working days</p>
                  </div>
                  <span>{formatCurrency(6.0)}</span>
                </label>

                <label
                  className={`checkout-shipping-option ${
                    shippingOption === "express" ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="shippingOption"
                    value="express"
                    checked={shippingOption === "express"}
                    onChange={() => setShippingOption("express")}
                  />
                  <div>
                    <strong>Express Delivery</strong>
                    <p>Fast delivery within 1 - 3 working days</p>
                  </div>
                  <span>{formatCurrency(12.0)}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="checkout-card">
          <div className="checkout-card-title-row">
            <h3>Voucher / Discount</h3>
            <span className="checkout-change-text">Change</span>
          </div>

          <div className="checkout-voucher-row">
            <div className="checkout-voucher-box">
              {usePromotion ? "Promotion Applied" : "No voucher selected"}
            </div>
            <div className="checkout-voucher-discount">
              - {formatCurrency(discountAmount)}
            </div>
          </div>
        </div>

        <div className="checkout-card">
          <div className="checkout-card-title-row">
            <h3>Payment Method</h3>
          </div>

          <div className="checkout-payment-methods">
            <button
              type="button"
              className={`checkout-payment-btn ${
                paymentMethod === "cash_on_delivery" ? "active" : ""
              }`}
              onClick={() => setPaymentMethod("cash_on_delivery")}
            >
              Cash on Delivery
            </button>

            {/* <button
              type="button"
              className={`checkout-payment-btn ${
                paymentMethod === "credit_debit_card" ? "active" : ""
              }`}
              onClick={() => setPaymentMethod("credit_debit_card")}
            >
              Credit / Debit Card
            </button> */}

            <button
              type="button"
              className={`checkout-payment-btn ${
                paymentMethod === "online_banking" ? "active" : ""
              }`}
              onClick={() => setPaymentMethod("online_banking")}
            >
              Online Banking
            </button>

            {/* <button
                type="button"
                className={`checkout-payment-btn ${
                  paymentMethod === "google_pay" ? "active" : ""
                }`}
                onClick={() => setPaymentMethod("google_pay")}
              >
                Google Pay
              </button> */}
          </div>

          <div className="checkout-payment-note">
            {paymentMethod === "cash_on_delivery" && (
              <span>
                Cash on Delivery selected. Your order will be placed directly.
              </span>
            )}

            {paymentMethod === "credit_debit_card" && (
              <span>You will be redirected to SecurePay card checkout.</span>
            )}

            {paymentMethod === "online_banking" && (
              <span>You will be redirected to SecurePay online banking.</span>
            )}

            {paymentMethod === "google_pay" && (
              <span>
                You will be redirected to SecurePay supported payment flow.
              </span>
            )}
          </div>
        </div>

        <div className="checkout-summary-bar">
          <div className="checkout-summary-lines">
            <div className="checkout-summary-line">
              <span>Merchandise Subtotal</span>
              <strong>{formatCurrency(merchandiseSubtotal)}</strong>
            </div>

            <div className="checkout-summary-line">
              <span>Shipping Total</span>
              <strong>{formatCurrency(shippingFee)}</strong>
            </div>

            <div className="checkout-summary-line">
              <span>Voucher Discount</span>
              <strong>- {formatCurrency(discountAmount)}</strong>
            </div>

            <div className="checkout-summary-line checkout-summary-total">
              <span>Total Payment</span>
              <strong>{formatCurrency(totalAmount)}</strong>
            </div>
          </div>

          <button
            type="button"
            className="checkout-place-order-btn"
            onClick={handleCheckout}
            disabled={loading || !cartItems.length}
          >
            {loading ? "Processing..." : "Place Order"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Checkout;
